import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { BRAND_MINT } from '@/lib/utils'
import {
  getFeatureFlagsApi,
  updateFeatureFlagApi,
  deleteFeatureFlagByIdApi,
  setOrgFeatureFlagApi,
  type FeatureFlag,
} from '@/lib/api'
import {
  LoaderIcon,
  FlagIcon,
  PlusIcon,
  Trash2Icon,
  SaveIcon,
  XIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  GlobeIcon,
  BuildingIcon,
  EditIcon,
  ClockIcon,
} from 'lucide-react'

type Tab = 'global' | 'overrides'

function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatValue(val: string | number | boolean | object | null): string {
  if (val === null || val === undefined) return '—'
  if (typeof val === 'boolean') return val ? 'true' : 'false'
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

export const Route = createFileRoute('/dashboard/admin/feature-flags')({
  component: () => {
    const [globalFlags, setGlobalFlags] = useState<FeatureFlag[]>([])
    const [orgOverrides, setOrgOverrides] = useState<FeatureFlag[]>([])
    const [loading, setLoading] = useState(true)
    const [tab, setTab] = useState<Tab>('global')

    // New flag creation
    const [showCreate, setShowCreate] = useState(false)
    const [newKey, setNewKey] = useState('')
    const [newValue, setNewValue] = useState('')
    const [newDesc, setNewDesc] = useState('')
    const [creating, setCreating] = useState(false)

    // Edit flag
    const [editingKey, setEditingKey] = useState<string | null>(null)
    const [editValue, setEditValue] = useState('')
    const [editDesc, setEditDesc] = useState('')
    const [saving, setSaving] = useState(false)

    // Delete confirmation
    const [confirmDelete, setConfirmDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Org override
    const [overrideKey, setOverrideKey] = useState<string | null>(null)
    const [overrideOrgId, setOverrideOrgId] = useState('')
    const [overrideValue, setOverrideValue] = useState('')
    const [creatingOverride, setCreatingOverride] = useState(false)

    const loadFlags = async () => {
      try {
        const res = await getFeatureFlagsApi()
        setGlobalFlags(res.global)
        setOrgOverrides(res.org_overrides)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load feature flags')
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      loadFlags()
    }, [])

    const handleCreate = async () => {
      if (!newKey.trim()) { toast.error('Flag key is required'); return }
      setCreating(true)
      try {
        // Try parsing as JSON for structured values
        let value: string | number | boolean | object = newValue.trim()
        if (value === 'true') value = true
        else if (value === 'false') value = false
        else if (/^\d+$/.test(value as string)) value = parseInt(value as string, 10)
        else if (/^\d+\.\d+$/.test(value as string)) value = parseFloat(value as string)
        else {
          try { value = JSON.parse(value as string) } catch {}
        }

        await updateFeatureFlagApi(newKey.trim(), {
          value,
          description: newDesc.trim() || undefined,
        })
        toast.success(`Feature flag "${newKey}" created.`)
        setShowCreate(false)
        setNewKey('')
        setNewValue('')
        setNewDesc('')
        await loadFlags()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create feature flag')
      } finally {
        setCreating(false)
      }
    }

    const handleEdit = async (key: string) => {
      setSaving(true)
      try {
        let value: string | number | boolean | object = editValue.trim()
        if (value === 'true') value = true
        else if (value === 'false') value = false
        else if (/^\d+$/.test(value as string)) value = parseInt(value as string, 10)
        else if (/^\d+\.\d+$/.test(value as string)) value = parseFloat(value as string)
        else {
          try { value = JSON.parse(value as string) } catch {}
        }

        await updateFeatureFlagApi(key, {
          value,
          description: editDesc.trim() || undefined,
        })
        toast.success(`Feature flag "${key}" updated.`)
        setEditingKey(null)
        await loadFlags()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update feature flag')
      } finally {
        setSaving(false)
      }
    }

    const handleDelete = async (flagId: number) => {
      setDeleting(true)
      try {
        const res = await deleteFeatureFlagByIdApi(flagId)
        toast.success('Feature flag deleted.')
        setConfirmDelete(null)
        await loadFlags()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete feature flag')
      } finally {
        setDeleting(false)
      }
    }

    const handleAddOverride = async () => {
      if (!overrideOrgId.trim()) { toast.error('Org ID is required'); return }
      setCreatingOverride(true)
      try {
        let value: string | number | boolean | object = overrideValue.trim()
        if (value === 'true') value = true
        else if (value === 'false') value = false
        else if (/^\d+$/.test(value as string)) value = parseInt(value as string, 10)
        else if (/^\d+\.\d+$/.test(value as string)) value = parseFloat(value as string)
        else {
          try { value = JSON.parse(value as string) } catch {}
        }

        await setOrgFeatureFlagApi(overrideKey!, parseInt(overrideOrgId, 10), { value })
        toast.success(`Override set for "${overrideKey}" on org #${overrideOrgId}.`)
        setOverrideKey(null)
        setOverrideOrgId('')
        setOverrideValue('')
        await loadFlags()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to set org override')
      } finally {
        setCreatingOverride(false)
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Feature flags</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading feature flags…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Feature flags</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage feature toggles and org-scoped overrides.
                </p>
              </div>
              <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5 shrink-0">
                <PlusIcon className="size-4" />
                New flag
              </Button>
            </div>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="mb-6 flex gap-1 rounded-lg bg-muted p-1 w-fit">
          <button onClick={() => setTab('global')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'global' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <span className="inline-flex items-center gap-1.5">
              <GlobeIcon className="size-3.5" />
              Global flags
              {globalFlags.length > 0 && <span className="ml-1 text-xs text-muted-foreground">({globalFlags.length})</span>}
            </span>
          </button>
          <button onClick={() => setTab('overrides')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'overrides' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <span className="inline-flex items-center gap-1.5">
              <BuildingIcon className="size-3.5" />
              Org overrides
              {orgOverrides.length > 0 && <span className="ml-1 text-xs text-muted-foreground">({orgOverrides.length})</span>}
            </span>
          </button>
        </div>

        {tab === 'global' ? (
          <>
            {globalFlags.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
                <FlagIcon className="size-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No feature flags configured</p>
                <p className="text-xs text-muted-foreground">Create a new flag to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {globalFlags.map((flag) => {
                  const isEditing = editingKey === flag.flag_key
                  return (
                    <div key={flag.id} className="rounded-xl border border-border bg-card p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <FlagIcon className="size-4 shrink-0 text-primary" />
                            <code className="rounded bg-muted px-2 py-0.5 text-sm font-semibold text-foreground font-mono">
                              {flag.flag_key}
                            </code>
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                              <GlobeIcon className="size-3" />
                              Global
                            </span>
                          </div>

                          {isEditing ? (
                            <div className="mt-3 space-y-3">
                              <div>
                                <label className="mb-1 block text-xs font-medium text-muted-foreground">Value</label>
                                <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)}
                                  className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-mono outline-none focus:border-primary" />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs font-medium text-muted-foreground">Description</label>
                                <input type="text" value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
                                  className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleEdit(flag.flag_key)} disabled={saving} className="gap-1.5">
                                  {saving ? <LoaderIcon className="size-4 animate-spin" /> : <SaveIcon className="size-4" />}
                                  Save
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setEditingKey(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {flag.description && (
                                <p className="mt-1 text-xs text-muted-foreground">{flag.description}</p>
                              )}
                              <div className="mt-2 flex items-center gap-2">
                                <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-mono tabular-nums">
                                  {formatValue(flag.flag_value)}
                                </span>
                                <span className="text-[10px] text-muted-foreground/60">
                                  Updated {formatTimestamp(flag.updated_at)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {!isEditing && (
                          <div className="flex shrink-0 gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => {
                                setEditingKey(flag.flag_key)
                                setEditValue(formatValue(flag.flag_value))
                                setEditDesc(flag.description || '')
                              }}
                              className="size-8 p-0"
                              title="Edit"
                            >
                              <EditIcon className="size-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setOverrideKey(flag.flag_key)}
                              className="size-8 p-0 text-muted-foreground"
                              title="Set org override"
                            >
                              <BuildingIcon className="size-4" />
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => setConfirmDelete(flag.id)}
                              className="size-8 p-0 text-destructive"
                              title="Delete"
                            >
                              <Trash2Icon className="size-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {orgOverrides.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
                <BuildingIcon className="size-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No org overrides configured</p>
                <p className="text-xs text-muted-foreground">Click the building icon on any global flag to set an org-scoped override.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orgOverrides.map((flag) => (
                  <div key={flag.id} className="rounded-xl border border-border bg-card p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
                        <BuildingIcon className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <code className="rounded bg-muted px-2 py-0.5 text-sm font-semibold text-foreground font-mono">
                            {flag.flag_key}
                          </code>
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600">
                            <BuildingIcon className="size-3" />
                            Org #{flag.org_id}{flag.org_name ? ` · ${flag.org_name}` : ''}
                          </span>
                        </div>
                        <div className="mt-2">
                          <span className="rounded-md bg-muted px-2.5 py-1 text-xs font-mono tabular-nums">
                            {formatValue(flag.flag_value)}
                          </span>
                          <span className="ml-2 text-[10px] text-muted-foreground/60">
                            Override set {formatTimestamp(flag.updated_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Create new flag dialog ── */}
        <Dialog open={showCreate} onClose={() => setShowCreate(false)}
          title="Create new feature flag"
          description="Add a global feature flag. Org-specific overrides can be added later."
          footer={
            <>
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={creating} className="gap-1.5">
                {creating ? <LoaderIcon className="size-4 animate-spin" /> : <PlusIcon className="size-4" />}
                Create
              </Button>
            </>
          }
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Flag key *</label>
              <input type="text" value={newKey} onChange={(e) => setNewKey(e.target.value)}
                autoFocus
                placeholder="e.g. new_onboarding_flow"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Value</label>
              <input type="text" value={newValue} onChange={(e) => setNewValue(e.target.value)}
                placeholder="true, false, 42, or any JSON value"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
              />
              <p className="mt-1 text-[10px] text-muted-foreground/60">
                Boolean, number, or JSON. Strings without quotes are treated as-is.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Description</label>
              <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                placeholder="What this flag controls"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>
        </Dialog>

        {/* ── Delete confirmation dialog ── */}
        <DestructiveConfirm
          open={confirmDelete !== null}
          onClose={() => setConfirmDelete(null)}
          title="Delete feature flag?"
          description={`This will permanently delete this feature flag (#${confirmDelete}) and all its org overrides.`}
          confirmLabel="Delete"
          onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
          loading={deleting}
        />

        {/* ── Set org override dialog ── */}
        <Dialog open={overrideKey !== null} onClose={() => setOverrideKey(null)}
          title="Set org-scoped override"
          description={`Override the value of "${overrideKey}" for a specific organization.`}
          footer={
            <>
              <Button variant="outline" size="sm" onClick={() => setOverrideKey(null)}>Cancel</Button>
              <Button size="sm" onClick={handleAddOverride} disabled={creatingOverride} className="gap-1.5">
                {creatingOverride ? <LoaderIcon className="size-4 animate-spin" /> : <SaveIcon className="size-4" />}
                Set override
              </Button>
            </>
          }
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Organization ID *</label>
              <input type="number" value={overrideOrgId} onChange={(e) => setOverrideOrgId(e.target.value)}
                placeholder="e.g. 5"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary tabular-nums"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Override value *</label>
              <input type="text" value={overrideValue} onChange={(e) => setOverrideValue(e.target.value)}
                placeholder="true, false, 42, or any JSON value"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary"
              />
            </div>
          </div>
        </Dialog>
      </div>
    )
  },
})
